import React from "react"
import { useTranslation } from "react-i18next"

import { Bike } from "models"
import {
	bikesNotifications,
	NOTIFICATION_PURCHASE_VALIDATION,
	NOTIFICATION_NEW_PROPERTY_VALIDATION,
	NOTIFICATION_BIKE_RETURN_VALIDATION,
	NOTIFICATION_BIKE_BUYBACK_VALIDATION,
} from "utils/constants/bike"

import { NotificationActions } from "./NotificationActions"
import { Button, Markdown } from "components/Elements"
import { BikeCard } from "../Card"

type NotificationsProps = {
	bikes: Bike[]
	refreshBikes: () => Promise<void>
}

const INITIAL_BIKES_TO_DISPLAY = 2

export const Notifications: React.FC<NotificationsProps> = ({ bikes, refreshBikes }) => {
	const [showAllBikes, setShowAllBikes] = React.useState<boolean>(false)
	const filteredBikes = showAllBikes ? bikes : bikes.slice(0, INITIAL_BIKES_TO_DISPLAY)

	const { t } = useTranslation()

	const getBlockTitle = (notificationType: string) => {
		return bikesNotifications.find((notification) => notification.id === notificationType)
			?.title
	}

	const getBikeDescription = (bike: Bike) => {
		switch (bike.bike_action_needed_code) {
			case NOTIFICATION_PURCHASE_VALIDATION:
			case NOTIFICATION_NEW_PROPERTY_VALIDATION:
				return (
					<Markdown>
						{t("notification.new_bike_description", {
							ns: "bike",
							bikeId: bike.bike_id,
							brand: bike.bike_description.brand,
							model: bike.bike_description.model,
							type: bike.bike_description.gear_type,
							color: bike.bike_description.color,
						})}
					</Markdown>
				)
			case NOTIFICATION_BIKE_RETURN_VALIDATION:
				return (
					<Markdown>
						{t("notification.back_store_description", {
							ns: "bike",
							store: bike.property_transfer_request.social_reason,
							bikeId: bike.bike_id,
							brand: bike.bike_description.brand,
							model: bike.bike_description.model,
							type: bike.bike_description.gear_type,
							color: bike.bike_description.color,
						})}
					</Markdown>
				)
			case NOTIFICATION_BIKE_BUYBACK_VALIDATION:
				return (
					<Markdown>
						{t("notification.buy_bike_description", {
							ns: "bike",
							store: bike.property_transfer_request.social_reason,
							bikeId: bike.bike_id,
							brand: bike.bike_description.brand,
							model: bike.bike_description.model,
							type: bike.bike_description.gear_type,
							color: bike.bike_description.color,
						})}
					</Markdown>
				)
			default:
				return <></>
		}
	}

	return (
		<>
			<div className="c-h1 leading-tight mb-4 md:mb-10">
				{t("notification.title", { ns: "bike" })} ({bikes.length})
			</div>

			<div className="flex justify-between flex-wrap mb-20">
				{filteredBikes.map((bike: Bike, index: number) => (
					<div className="w-full lg:w-1/2 md:px-4 mb-6" key={index}>
						<div className="c-block py-4 px-6">
							<h2 className="c-h2 text-primary pb-4">
								{getBlockTitle(bike.bike_action_needed_code)}
							</h2>

							<div className="flex justify-center flex-wrap items-center">
								<BikeCard bike={bike} />

								<div className="flex-1 text-center m-6">
									{getBikeDescription(bike)}

									<NotificationActions bike={bike} refreshBikes={refreshBikes} />
								</div>
							</div>
						</div>
					</div>
				))}

				{bikes.length > INITIAL_BIKES_TO_DISPLAY && (
					<div className="flex justify-center w-full mt-6">
						<Button
							onClick={() => setShowAllBikes((prevShowAllBikes) => !prevShowAllBikes)}
							className="px-12 py-1"
							size="small"
							variant={showAllBikes ? "primary" : "primaryBordered"}
						>
							{showAllBikes
								? t("notification.show_less", {
										ns: "bike",
								  })
								: t("notification.show_more", {
										ns: "bike",
								  })}
						</Button>
					</div>
				)}
			</div>
		</>
	)
}
